import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  AlertTriangle,
  RefreshCw,
  Sparkles,
  Rocket,
  Building,
  ArrowUp,
  ArrowDown,
  CreditCard,
  Infinity,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { BrandedLoading, BrandedSpinner } from '@/components/common/BrandedLoading';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/useToast';
import { billingApi } from '@/services/billingApi';
import { subscriptionPlanApi } from '@/services/subscriptionApi';
import type { Subscription, BillingCycle } from '@/types/billing';
import type { SubscriptionPlan } from '@/types/subscription';
import { PlanComparisonTable } from '@/components/billing/PlanComparisonTable';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatCurrency = (amount: number, currency = 'USD') =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
  }).format(amount);

const formatCurrencySafe = (amount: unknown, currency = 'USD'): string => {
  const num = typeof amount === 'number' ? amount : Number(amount);
  if (!Number.isFinite(num)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: num % 1 === 0 ? 0 : 2,
  }).format(num);
};

const priceForCycle = (plan: SubscriptionPlan, cycle: BillingCycle) =>
  cycle === 'YEARLY' ? (plan.yearlyPrice || plan.monthlyPrice * 12) : plan.monthlyPrice;

const cycleSuffix = (cycle: BillingCycle) =>
  cycle === 'YEARLY' ? '/yr' : cycle === 'QUARTERLY' ? '/qtr' : '/mo';

const getFeaturesList = (plan: SubscriptionPlan): string[] => {
  if (!plan.features) return [];
  if (Array.isArray(plan.features)) return plan.features as string[];
  if (typeof plan.features === 'object') {
    return Object.entries(plan.features).map(([key, value]) => {
      if (typeof value === 'string') return value;
      if (typeof value === 'boolean' && value) return key;
      return `${key}: ${value}`;
    });
  }
  return [];
};

// Map iconName from seed data to lucide icon components
const ICON_MAP: Record<string, React.ElementType> = {
  sparkles: Sparkles,
  rocket: Rocket,
  building: Building,
};

/** Render a limit value: -1 or null/undefined = "Unlimited" */
const formatLimitValue = (value: number | undefined | null): React.ReactNode => {
  if (value === undefined || value === null || value === -1) {
    return (
      <span className="inline-flex items-center gap-1">
        <Infinity className="h-3 w-3" />
        Unlimited
      </span>
    );
  }
  return value.toLocaleString();
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const PlanChangePage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [selectedCycle, setSelectedCycle] = useState<BillingCycle>('MONTHLY');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    if (!user?.id) return;
    try {
      setIsLoading(true);
      setError(null);

      const [sub, plansResponse] = await Promise.all([
        billingApi.getActiveSubscription(user.id).catch((err) => {
          const message = (err as Error)?.message ?? '';
          if (message.includes('Authentication required')) throw err;
          // 404 = no subscription — treat as "no active subscription" for the UI
          return null;
        }),
        subscriptionPlanApi.getActive({ size: 50, sort: 'displayOrder,asc' }),
      ]);

      setSubscription(sub);

      const activePlans = (plansResponse.content ?? [])
        .filter((p) => p.status === 'ACTIVE')
        .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
      setPlans(activePlans);

      // Default billing cycle to current subscription's cycle
      if (sub?.billingCycle) {
        setSelectedCycle(sub.billingCycle);
      }
    } catch (err: unknown) {
      const message = (err as Error)?.message ?? '';
      if (message.includes('Authentication required')) {
        navigate(`/login?next=${encodeURIComponent(location.pathname)}`, { replace: true });
        return;
      }
      setError(message || 'Failed to load plans');
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, navigate, location.pathname]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSelectPlan = (plan: SubscriptionPlan) => {
    if (subscription && plan.id === subscription.subscriptionPlanId) return; // already on this plan
    setSelectedPlan(plan);
    setShowConfirmDialog(true);
  };

  const handleConfirmChange = async () => {
    if (!subscription || !selectedPlan) return;
    try {
      setIsSubmitting(true);
      await billingApi.changePlan(subscription.id, {
        newPlanId: selectedPlan.id,
        billingCycle: selectedCycle,
      });
      toast({
        title: 'Plan changed successfully',
        description: `You are now on the ${selectedPlan.displayName} plan.`,
      });
      setShowConfirmDialog(false);
      navigate('/billing');
    } catch (err: unknown) {
      toast({
        title: 'Plan change failed',
        description: (err as Error)?.message ?? 'Please try again later.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentPlan = subscription
    ? plans.find((p) => p.id === subscription.subscriptionPlanId) ?? null
    : null;

  const currentPriceForSelectedCycle = currentPlan
    ? priceForCycle(currentPlan, selectedCycle)
    : subscription
      ? Number(subscription.amount)
      : 0;

  const isCurrentPlan = (plan: SubscriptionPlan) =>
    subscription?.subscriptionPlanId === plan.id;

  const isUpgrade = (plan: SubscriptionPlan) => {
    if (!subscription) return true;
    const current = Number.isFinite(currentPriceForSelectedCycle) ? currentPriceForSelectedCycle : 0;
    return priceForCycle(plan, selectedCycle) > current;
  };

  // ---- Loading ----
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
        <Header />
        <main className="flex flex-col items-center justify-center min-h-[60vh] py-12 px-4">
          <BrandedLoading message="Loading subscription plans…" iconSize="md" />
        </main>
        <Footer />
      </div>
    );
  }

  // ---- Error ----
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
        <Header />
        <main className="flex flex-col items-center justify-center min-h-[60vh] py-12 px-4">
          <div className="text-center space-y-4 max-w-md">
            <AlertTriangle className="h-12 w-12 text-destructive mx-auto" />
            <p className="text-destructive">{error}</p>
            <Button onClick={fetchData}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      <Header />
      <main className="py-8 pt-20 sm:py-12 sm:pt-24">
        <div className="container max-w-6xl mx-auto px-4 sm:px-6 space-y-8">
          {/* Back + heading */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Button variant="ghost" size="sm" onClick={() => navigate('/billing')}>
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Billing
            </Button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center space-y-2"
          >
            <h1 className="text-3xl sm:text-4xl font-bold text-foreground">Change Your Plan</h1>
            <p className="text-muted-foreground max-w-lg mx-auto">
              {subscription
                ? 'Choose a new plan below. Changes take effect at the start of your next billing period.'
                : 'Select a plan to get started.'}
            </p>
          </motion.div>

          {/* Billing cycle toggle */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="flex justify-center"
          >
            <div className="inline-flex items-center bg-muted rounded-lg p-1">
              <button
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  selectedCycle === 'MONTHLY'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                onClick={() => setSelectedCycle('MONTHLY')}
              >
                Monthly
              </button>
              <button
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  selectedCycle === 'YEARLY'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                onClick={() => setSelectedCycle('YEARLY')}
              >
                Yearly
                {(() => {
                  const maxSavings = plans.reduce((max, p) => Math.max(max, p.savingsPercentage ?? 0), 0);
                  return (
                    <span className="ml-1 text-xs text-emerald-600 font-semibold">
                      Save {maxSavings > 0 ? `${maxSavings}%` : '20%'}
                    </span>
                  );
                })()}
              </button>
            </div>
          </motion.div>

          {/* Current plan indicator */}
          {subscription && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.08 }}
              className="text-center text-sm text-muted-foreground"
            >
              <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1">
                <CreditCard className="h-3.5 w-3.5" />
                Current: {formatCurrencySafe(currentPriceForSelectedCycle, subscription.currency)}
                {cycleSuffix(subscription.billingCycle)}
              </span>
            </motion.div>
          )}

          {/* Plans grid */}
          {plans.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="max-w-md mx-auto">
                <CardContent className="py-12 text-center space-y-3">
                  <CreditCard className="h-12 w-12 mx-auto text-muted-foreground" />
                  <h2 className="text-lg font-semibold">No plans available</h2>
                  <p className="text-sm text-muted-foreground">
                    There are no subscription plans available at this time.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {plans.map((plan, index) => {
                const isCurrent = isCurrentPlan(plan);
                const isPopular = plan.isPopular || false;
                const badgeLabel = isCurrent ? 'Current Plan' : (plan.badgeText || (isPopular ? 'Popular' : null));
                const price = priceForCycle(plan, selectedCycle);
                const monthlyEquiv = selectedCycle === 'YEARLY' && plan.yearlyPrice
                  ? Math.round((plan.yearlyPrice / 12) * 100) / 100
                  : null;
                const featureHighlights = plan.featureHighlights ?? [];
                const legacyFeatures = featureHighlights.length === 0 ? getFeaturesList(plan) : [];
                const IconComponent = plan.iconName ? ICON_MAP[plan.iconName] ?? null : null;
                const isEnterprise = plan.planType === 'ENTERPRISE';
                const isFree = plan.planType === 'FREE' || plan.monthlyPrice === 0;

                return (
                  <motion.div
                    key={plan.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 + index * 0.05 }}
                    className={badgeLabel && !isCurrent ? 'md:-mt-2' : ''}
                  >
                    <Card
                      className={`relative h-full transition-all duration-200 ${
                        isCurrent
                          ? 'border-2 border-primary/50 bg-primary/5'
                          : badgeLabel
                          ? 'border-2 border-primary/30 shadow-lg hover:shadow-xl'
                          : 'hover:shadow-md hover:border-primary/20'
                      }`}
                    >
                      {badgeLabel && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                          <Badge className={`px-3 py-0.5 rounded-full text-xs font-medium shadow border-0 ${
                            isCurrent
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-gradient-to-r from-purple-600 to-primary text-white'
                          }`}>
                            {!isCurrent && <Sparkles className="w-3 h-3 mr-1" />}
                            {badgeLabel}
                          </Badge>
                        </div>
                      )}

                      <CardHeader className="text-center pt-6 pb-4">
                        {/* Icon */}
                        {IconComponent && (
                          <div className="flex justify-center mb-2">
                            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                              <IconComponent className="h-5 w-5 text-primary" />
                            </div>
                          </div>
                        )}

                        <CardTitle className="text-xl">{plan.displayName}</CardTitle>

                        {/* Tagline */}
                        {plan.tagline && (
                          <p className="text-xs font-medium text-primary/80 mt-1">{plan.tagline}</p>
                        )}

                        <CardDescription className="min-h-[2rem]">
                          {plan.description}
                        </CardDescription>

                        <div className="pt-2">
                          {isFree ? (
                            <>
                              <span className="text-3xl font-bold">$0</span>
                              <span className="text-muted-foreground text-sm">/mo</span>
                            </>
                          ) : isEnterprise ? (
                            <span className="text-3xl font-bold">Custom</span>
                          ) : (
                            <>
                              <span className="text-3xl font-bold">
                                {selectedCycle === 'YEARLY' && monthlyEquiv !== null
                                  ? formatCurrency(monthlyEquiv, plan.currency)
                                  : formatCurrency(price, plan.currency)}
                              </span>
                              <span className="text-muted-foreground text-sm">/mo</span>
                              {selectedCycle === 'YEARLY' && monthlyEquiv !== null && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  {formatCurrency(plan.yearlyPrice, plan.currency)}/yr
                                </p>
                              )}
                            </>
                          )}
                        </div>

                        {/* Savings badge */}
                        {selectedCycle === 'YEARLY' && plan.savingsPercentage && plan.savingsPercentage > 0 && (
                          <Badge variant="secondary" className="text-emerald-600 bg-emerald-50 dark:bg-emerald-950 border-0 mt-1">
                            Save {plan.savingsPercentage}%
                          </Badge>
                        )}

                        {plan.trialEnabled && plan.trialDurationDays && !isCurrent && (
                          <p className="text-xs text-primary font-medium mt-1">
                            {plan.trialDurationDays}-day free trial
                          </p>
                        )}

                        {/* Recommended for */}
                        {plan.recommendedFor && (
                          <p className="text-[11px] text-muted-foreground italic mt-2">
                            Best for: {plan.recommendedFor}
                          </p>
                        )}
                      </CardHeader>

                      <CardContent className="space-y-4">
                        {/* Feature highlights with checkmarks */}
                        {featureHighlights.length > 0 && (
                          <ul className="space-y-2">
                            {featureHighlights.map((feature, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-sm">
                                <Check className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
                                <span>{feature}</span>
                              </li>
                            ))}
                          </ul>
                        )}

                        {/* Legacy features fallback */}
                        {legacyFeatures.length > 0 && (
                          <ul className="space-y-2">
                            {legacyFeatures.map((feature, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-sm">
                                <Check className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
                                <span>{feature}</span>
                              </li>
                            ))}
                          </ul>
                        )}

                        {/* Typed limits */}
                        {(plan.dailyTokenLimit !== undefined || plan.monthlyTokenLimit !== undefined || plan.maxCoachSessionsPerDay !== undefined || plan.maxLearningPaths !== undefined) && (
                          <div className="pt-2 border-t space-y-1">
                            {plan.dailyTokenLimit !== undefined && (
                              <div className="flex items-center justify-between text-xs">
                                <span className="text-muted-foreground">Daily Tokens</span>
                                <span className="font-medium">{formatLimitValue(plan.dailyTokenLimit)}</span>
                              </div>
                            )}
                            {plan.monthlyTokenLimit !== undefined && (
                              <div className="flex items-center justify-between text-xs">
                                <span className="text-muted-foreground">Monthly Tokens</span>
                                <span className="font-medium">{formatLimitValue(plan.monthlyTokenLimit)}</span>
                              </div>
                            )}
                            {plan.maxCoachSessionsPerDay !== undefined && (
                              <div className="flex items-center justify-between text-xs">
                                <span className="text-muted-foreground">Coach Sessions/Day</span>
                                <span className="font-medium">{formatLimitValue(plan.maxCoachSessionsPerDay)}</span>
                              </div>
                            )}
                            {plan.maxLearningPaths !== undefined && (
                              <div className="flex items-center justify-between text-xs">
                                <span className="text-muted-foreground">Learning Paths</span>
                                <span className="font-medium">{formatLimitValue(plan.maxLearningPaths)}</span>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Legacy limits fallback */}
                        {plan.limits && Object.keys(plan.limits).length > 0 && plan.dailyTokenLimit === undefined && plan.monthlyTokenLimit === undefined && (
                          <div className="pt-2 border-t space-y-1">
                            {Object.entries(plan.limits).map(([key, value]) => (
                              <div key={key} className="flex items-center justify-between text-xs">
                                <span className="text-muted-foreground capitalize">
                                  {key.replace(/([A-Z])/g, ' $1').trim()}
                                </span>
                                <span className="font-medium">{value === -1 ? formatLimitValue(-1) : value}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        <Button
                          className="w-full"
                          variant={isCurrent ? 'outline' : (badgeLabel && !isCurrent) ? 'default' : 'outline'}
                          disabled={isCurrent}
                          onClick={() => handleSelectPlan(plan)}
                        >
                          {isCurrent ? (
                            'Current Plan'
                          ) : isEnterprise ? (
                            <>
                              {plan.ctaText || 'Contact Sales'}
                              <ArrowRight className="h-4 w-4 ml-1" />
                            </>
                          ) : isUpgrade(plan) ? (
                            <>
                              <ArrowUp className="h-4 w-4 mr-1" />
                              {plan.ctaText || 'Upgrade'}
                            </>
                          ) : (
                            <>
                              <ArrowDown className="h-4 w-4 mr-1" />
                              {plan.ctaText || 'Downgrade'}
                            </>
                          )}
                        </Button>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          )}

          {/* Plan comparison table */}
          {plans.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mt-12"
            >
              <h2 className="text-2xl font-bold text-foreground text-center mb-6">Compare Plans</h2>
              <PlanComparisonTable plans={plans} />
            </motion.div>
          )}
        </div>
      </main>
      <Footer />

      {/* Confirmation dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {selectedPlan && isUpgrade(selectedPlan) ? 'Upgrade' : 'Downgrade'} to{' '}
              {selectedPlan?.displayName}?
            </DialogTitle>
            <DialogDescription className="space-y-2 pt-2">
              {selectedPlan && !isUpgrade(selectedPlan) && (
                <span className="block text-amber-600 dark:text-amber-400 font-medium">
                  Downgrading may result in reduced features and limits. Changes take effect at the end of your current billing period.
                </span>
              )}
              {selectedPlan && isUpgrade(selectedPlan) && (
                <span className="block">
                  Your new plan will be prorated for the remaining time in your current billing cycle.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          {selectedPlan && (
            <div className="rounded-lg border bg-muted/30 p-4 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">New plan</span>
                <span className="font-medium">{selectedPlan.displayName}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Billing cycle</span>
                <span className="font-medium capitalize">{selectedCycle.toLowerCase()}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">New price</span>
                <span className="font-semibold">
                  {formatCurrency(priceForCycle(selectedPlan, selectedCycle), selectedPlan.currency)}
                  {cycleSuffix(selectedCycle)}
                </span>
              </div>
              {subscription && (
                <div className="flex items-center justify-between text-sm border-t pt-2 mt-2">
                  <span className="text-muted-foreground">Previous price</span>
                  <span className="line-through text-muted-foreground">
                    {formatCurrencySafe(subscription.amount, subscription.currency)}
                    {cycleSuffix(subscription.billingCycle)}
                  </span>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowConfirmDialog(false)}
              disabled={isSubmitting}
            >
              Keep Current Plan
            </Button>
            <Button
              onClick={handleConfirmChange}
              disabled={isSubmitting}
              variant={selectedPlan && !isUpgrade(selectedPlan) ? 'destructive' : 'default'}
            >
              {isSubmitting ? (
                <>
                  <BrandedSpinner className="mr-1" />
                  Processing...
                </>
              ) : (
                <>
                  Confirm Change
                  <ArrowRight className="h-4 w-4 ml-1" />
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlanChangePage;
